/**
 * TrpcContentReader — production adapter implementing
 * {@link ContentReaderPort} (TA15, the content-and-knowledge feature Phase A / PR B).
 *
 * Wraps the typed tRPC client (`@omaplus/shared-types/trpc` AppRouter)
 * and forwards each port method to the matching `content.*` procedure.
 * Returns shapes are field-for-field identical to the API's
 * `ContentSourcePort` — the wire-level mapping is performed by the
 * tRPC envelope (no transformer is configured, so JSON shapes round-trip
 * directly), and TypeScript verifies that the inferred client return
 * matches the port-declared shape at the seam.
 *
 * D-046 — React Query integration
 * ===============================
 *
 * Per D-046 (Decisions log), React Query is the port-backed async-data
 * layer for `apps/web`. The adapter itself is intentionally NOT a hook
 * and does NOT touch React Query directly. The split is:
 *
 *   - This adapter: a plain `class` that returns `Promise<T>` shapes
 *     declared by `ContentReaderPort`. It is the "fetcher" the consumer
 *     hooks call inside their `queryFn`. No React state, no cache
 *     concerns, no loading state.
 *   - TA16 hooks (`useCategories`, `useArticle`, `useCuratedCards`):
 *     each wraps `useContent()` (which resolves this
 *     adapter from `PortsProvider`) inside `useQuery({ queryKey, queryFn })`.
 *     The hook is where loading / error / cache / retry semantics live.
 *
 * Keeping the adapter free of React Query keeps the port boundary
 * uniform across runtime and tests. A test that swaps a stub adapter
 * via `renderAppWithPorts({ content: stub })` does not have to know
 * anything about React Query — the hook layer above is the only site
 * that sees it. This mirrors the API-side `ContentSourcePort` /
 * `SeededContentSource` split: the adapter is a fetcher, the layers
 * around it own the framework integration.
 *
 * `getArticleBySlug` returning `null`
 * -----------------------------------
 *
 * The port contract states that `null` means "no row matched". The API
 * router preserves this — it does NOT map `null` to tRPC `NOT_FOUND`
 * (see `apps/api/src/modules/content/router.ts` file header §"Error
 * mapping" and TA10 spec entry). The adapter therefore forwards the
 * `Article | null` return as-is; consumers discriminate via the
 * sentinel return rather than via a thrown error.
 *
 * Construction
 * ============
 *
 * The adapter takes a {@link WebTrpcClient} via constructor injection
 * (DI):
 *
 *   - Production: `useContentPort()` (in `apps/web/src/providers/`)
 *     constructs the client via `createWebTrpcClient()` once per React
 *     root and hands it to `new TrpcContentReader(client)`.
 *   - Tests: a hand-rolled mock client that satisfies the subset of
 *     `WebTrpcClient` the adapter actually uses (`content.<procedure>.query`)
 *     is supplied directly. No real network, no mock-server scaffolding.
 *
 * Authoritative spec references
 * =============================
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA15 (this adapter).
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being implemented).
 *   - `docs/governance/decisions/D-046.md` D-046 (React Query integration rules).
 */

import type { WebTrpcClient } from '../../trpc/client.js';
import type {
  Article,
  Banner,
  Benefit,
  Category,
  ContentReaderPort,
  CuratedCard,
  HomeButton,
  VideoListing,
} from '../ContentReaderPort.js';

export class TrpcContentReader implements ContentReaderPort {
  readonly #client: WebTrpcClient;

  /**
   * @param client - the typed tRPC client this adapter delegates to.
   *   Production wiring builds the client via
   *   `createWebTrpcClient()`; tests pass a hand-rolled mock that
   *   satisfies the subset of {@link WebTrpcClient} this adapter uses.
   */
  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async listCategories(opts?: { locale?: string }): Promise<readonly Category[]> {
    return this.#client.content.listCategories.query({ locale: opts?.locale });
  }

  async getArticleBySlug(slug: string, opts?: { locale?: string }): Promise<Article | null> {
    // The port contract reserves `null` for "no row matched". The API
    // router forwards the adapter's `null` unchanged (it does not map
    // null → NOT_FOUND), so the typed return shape is `Article | null`
    // and we forward it verbatim. Consumers discriminate via the
    // sentinel rather than via a thrown error.
    return this.#client.content.getArticleBySlug.query({ slug, locale: opts?.locale });
  }

  async listCuratedCards(opts?: {
    limit?: number;
    locale?: string;
  }): Promise<readonly CuratedCard[]> {
    return this.#client.content.listCuratedCards.query({
      locale: opts?.locale,
      limit: opts?.limit,
    });
  }

  async listVideoListings(opts?: {
    limit?: number;
    locale?: string;
  }): Promise<readonly VideoListing[]> {
    return this.#client.content.listVideoListings.query({
      locale: opts?.locale,
      limit: opts?.limit,
    });
  }

  async listBenefits(opts?: { limit?: number; locale?: string }): Promise<readonly Benefit[]> {
    return this.#client.content.listBenefits.query({
      locale: opts?.locale,
      limit: opts?.limit,
    });
  }

  async listHomeButtons(opts?: {
    limit?: number;
    locale?: string;
  }): Promise<readonly HomeButton[]> {
    return this.#client.content.listHomeButtons.query({
      locale: opts?.locale,
      limit: opts?.limit,
    });
  }

  async getPublishedBanner(opts?: { locale?: string }): Promise<Banner | null> {
    return this.#client.content.getPublishedBanner.query({ locale: opts?.locale });
  }
}
